import { useRef, useState } from "react";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { RecurrenceFieldValue } from "@common/components/filterPopover/subMenues/RecurrenceFieldSubMenu.tsx";
import { GetRecurrenceData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper.ts";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu.tsx";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";

// Column sort persisted alongside the filters so a table's ordering survives navigating away and back, the
// same way the filter selection does. Null until the user first sorts a column.
export interface FilterSortState {
    orderBy: string;
    order: "asc" | "desc";
}

interface FilterStateSnapshot {
    selectFilters: Record<string, Array<string>>;
    booleanFilters: Record<string, boolean | string>;
    textFilters: Record<string, string>;
    dateFilters: Record<string, DateFieldValue | null>;
    recurrenceFilter: RecurrenceFieldValue | null;
    filterOperators: Record<string, FilterOperatorUi>;
    sort: FilterSortState | null;
    // Filters left on screen without a value, so emptying a chip keeps it around to type into again.
    pinnedFilterIds: Array<string>;
}

const emptySnapshot: FilterStateSnapshot = {
    selectFilters: {},
    booleanFilters: {},
    textFilters: {},
    dateFilters: {},
    recurrenceFilter: null,
    filterOperators: {},
    sort: null,
    pinnedFilterIds: [],
};

function readSnapshot(key: string): FilterStateSnapshot {
    const item = sessionStorage.getItem(key);
    if (!item) return emptySnapshot;

    try {
        const parsed = JSON.parse(item);
        return {
            selectFilters: parsed.selectFilters ?? {},
            booleanFilters: parsed.booleanFilters ?? {},
            textFilters: parsed.textFilters ?? {},
            dateFilters: parsed.dateFilters ?? {},
            recurrenceFilter: parsed.recurrenceFilter ?? null,
            filterOperators: parsed.filterOperators ?? {},
            sort: parsed.sort ?? null,
            pinnedFilterIds: parsed.pinnedFilterIds ?? [],
        };
    } catch {
        return emptySnapshot;
    }
}

export function useFilterState(sessionStorageKey?: string) {
    const initial = sessionStorageKey ? readSnapshot(sessionStorageKey) : emptySnapshot;

    const [selectFilters, setSelectFilters] = useState<Record<string, Array<string>>>(initial.selectFilters);
    const [booleanFilters, setBooleanFilters] = useState<Record<string, boolean | string>>(initial.booleanFilters);
    const [textFilters, setTextFilters] = useState<Record<string, string>>(initial.textFilters);
    const [dateFilters, setDateFilters] = useState<Record<string, DateFieldValue | null>>(initial.dateFilters);
    const [recurrenceFilter, setRecurrenceFilter] = useState<RecurrenceFieldValue | null>(initial.recurrenceFilter);
    const [filterOperators, setFilterOperators] = useState<Record<string, FilterOperatorUi>>(initial.filterOperators);
    const [sort, setSort] = useState<FilterSortState | null>(initial.sort);
    const [pinnedFilterIds, setPinnedFilterIds] = useState<Array<string>>(initial.pinnedFilterIds);

    // Changing a filter and repinning it happen in the same event, so a writer building its snapshot from the
    // render's state would persist values captured before the other writer ran. Every write goes through this ref.
    const latestSnapshot = useRef<FilterStateSnapshot>(initial);

    const [previousKey, setPreviousKey] = useState(sessionStorageKey);
    if (sessionStorageKey !== previousKey) {
        setPreviousKey(sessionStorageKey);
        const snapshot = sessionStorageKey ? readSnapshot(sessionStorageKey) : emptySnapshot;
        latestSnapshot.current = snapshot;
        setSelectFilters(snapshot.selectFilters);
        setBooleanFilters(snapshot.booleanFilters);
        setTextFilters(snapshot.textFilters);
        setDateFilters(snapshot.dateFilters);
        setRecurrenceFilter(snapshot.recurrenceFilter);
        setFilterOperators(snapshot.filterOperators);
        setSort(snapshot.sort);
        setPinnedFilterIds(snapshot.pinnedFilterIds);
    }

    // Every writer persists the whole blob, so each must carry the others' current values forward — otherwise
    // changing a filter would drop the stored sort, and sorting would drop the stored filters. Spreading
    // latestSnapshot.current is what enforces that.
    function persist(snapshot: FilterStateSnapshot) {
        latestSnapshot.current = snapshot;
        if (sessionStorageKey) sessionStorage.setItem(sessionStorageKey, JSON.stringify(snapshot));
    }

    function onFilterChange(options: Array<FilterOption>) {
        const nextSelect: Record<string, Array<string>> = {};
        const nextBoolean: Record<string, boolean | string> = {};
        const nextText: Record<string, string> = {};
        const nextDate: Record<string, DateFieldValue | null> = {};
        const nextOperators: Record<string, FilterOperatorUi> = {};
        let nextRecurrence: RecurrenceFieldValue | null = recurrenceFilter;

        options.forEach((option) => {
            if (option.filterOperator !== undefined) {
                nextOperators[option.id] = option.filterOperator;
            }
            switch (option.type) {
                case FilterOptionType.SelectField:
                    nextSelect[option.id] = option.value ? (option.value as Array<string>) : [];
                    break;
                case FilterOptionType.Boolean:
                    nextBoolean[option.id] = option.value ?? "";
                    break;
                case FilterOptionType.TextField:
                    nextText[option.id] = option.value ?? "";
                    break;
                case FilterOptionType.DateField: {
                    const dateValue = option.value;
                    nextDate[option.id] = dateValue && typeof dateValue === "object" ? (dateValue as DateFieldValue) : null;
                    break;
                }
                case FilterOptionType.RecurrenceField: {
                    const recurrenceValue = option.value ? GetRecurrenceData(option.value) : null;
                    nextRecurrence = recurrenceValue?.from || recurrenceValue?.to || recurrenceValue?.recurrenceFilterEnum ? recurrenceValue : null;
                    break;
                }
            }
        });

        setSelectFilters(nextSelect);
        setBooleanFilters(nextBoolean);
        setTextFilters(nextText);
        setDateFilters(nextDate);
        setRecurrenceFilter(nextRecurrence);
        setFilterOperators(nextOperators);

        persist({
            ...latestSnapshot.current,
            selectFilters: nextSelect,
            booleanFilters: nextBoolean,
            textFilters: nextText,
            dateFilters: nextDate,
            recurrenceFilter: nextRecurrence,
            filterOperators: nextOperators,
        });
    }

    function onSortChange(next: FilterSortState) {
        setSort(next);
        persist({ ...latestSnapshot.current, sort: next });
    }

    function onPinnedFilterIdsChange(ids: Array<string> | undefined) {
        const next = ids ?? [];
        setPinnedFilterIds(next);
        persist({ ...latestSnapshot.current, pinnedFilterIds: next });
    }

    return {
        selectFilters,
        booleanFilters,
        textFilters,
        dateFilters,
        recurrenceFilter,
        filterOperators,
        onFilterChange,
        sort,
        onSortChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    };
}
